import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUser } from "app/slice/selectors";
import { createAbsoluteUrl } from "utils/constants";
import {
  ACTIVATION_ENTRY_SEARCH_PARAM,
  ActivationEntryType,
} from "app/pages/Customer/Activation/activation-entry-types";

import { Separator } from "app/components/ui/separator";
import { Button } from "app/components/ui/button";
import { Badge } from "app/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "app/components/ui/dialog";

const activateOptions = [
  {
    icon: "/assets/preloaded-sim.svg",
    altText: "Sim Card",
    title: "Preloaded SIM card activation (new accounts only)",
    description: "Pick this option for SIM cards preloaded with a service plan",
    isComingSoon: false,
  },
  {
    icon: "/assets/phone-office.svg",
    altText: "Phone Office",
    title: "Activate fixed phone base*",
    description: "Choose this option if you purchased a base from a reseller",
    isComingSoon: false,
  },
];

interface Props {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export const ActivateDialog = ({
  isOpen,
  onOpenChange,
  hideTrigger,
}: Props) => {
  const [internalShowDialog, setInternalShowDialog] = useState(false);

  const showDialog = isOpen !== undefined ? isOpen : internalShowDialog;
  const setShowDialog = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalShowDialog(open);
    }
  };

  const [selectedOptionTitle, setSelectedOptionTitle] = useState<string | null>(
    null,
  );
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const handleContinue = () => {
    if (selectedOptionTitle === activateOptions[0].title) {
      navigate(
        `/activation?${ACTIVATION_ENTRY_SEARCH_PARAM}=${ActivationEntryType.PreloadedSim}`,
      );
    } else if (selectedOptionTitle === activateOptions[1].title) {
      navigate(
        `/activation?${ACTIVATION_ENTRY_SEARCH_PARAM}=${ActivationEntryType.FixedPhoneBase}`,
      );
    }
    setShowDialog(false);
    setSelectedOptionTitle(null);
  };

  return (
    <React.Fragment>
      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setSelectedOptionTitle(null);
          }
        }}
      >
        {!hideTrigger && (
          <>
            <Button
              type="button"
              variant="ghost"
              className={`lg:hidden justify-between hover:bg-transparent w-full text-sm leading-[1.8125rem] hover:text-[#FE4D01] ${
                showDialog
                  ? "text-[#1D1D1B] font-semibold"
                  : "text-[#464646] font-medium"
              }`}
              onClick={() => setShowDialog(true)}
            >
              Activate
              <img
                src="/assets/chevron-down.svg"
                alt="Chevron Down"
                className="-rotate-90 size-5 shrink-0"
                aria-hidden="true"
              />
            </Button>
            <Button
              type="button"
              variant="ghost"
              className={`hidden lg:inline-flex hover:bg-transparent text-xs sm:text-[0.825rem] lg:text-sm font-normal leading-4 sm:leading-[1.125rem] lg:leading-5 hover:text-[#FE4D01] whitespace-nowrap ${
                showDialog ? "text-[#1D1D1B]" : "text-[#464646]"
              }`}
              onClick={() => setShowDialog(true)}
            >
              Activate
            </Button>
          </>
        )}
        <DialogContent
          className="reltive top-[45%] gap-0 bg-white w-[95%] sm:max-w-[34.125rem] p-4 pt-3 sm:p-5 sm:pt-[0.8125rem] lg:p-6 lg:pt-[0.875rem] text-center"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogClose className="absolute top-3.5 md:top-4 right-5 md:right-6 rounded-full">
            <img
              src="/assets/close-circle.webp"
              className="flex-shrink-0 w-5 h-5 rounded-full opacity-90 md:w-6 md:h-6 hover:opacity-100"
              alt="Close"
            />
          </DialogClose>
          <DialogHeader className="flex-col-reverse sm:flex-row flex-wrap justify-between gap-x-4 gap-y-2 items-start p-0 bg-transparent text-left mb-3.5 sm:mb-4 lg:mb-[1.125rem]">
            <DialogTitle className="max-w-full font-mobile text-black text-xl sm:text-[1.3125rem] lg:text-[1.375rem] font-bold leading-6 sm:leading-7 tracking-[-0.015rem]">
              Make a selection to proceed
            </DialogTitle>
            <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 mt-1 sm:mt-1.5 pr-8 sm:pr-9 md:pr-10">
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto font-bold text-[#FE4D01] hover:text-[#E44501] text-[0.71875rem] sm:text-xs leading-4 uppercase underline underline-offset-2 hover:decoration-[#E44501]"
                onClick={() => {
                  navigate(user ? "/my-account/dashboard" : "/login");
                  setShowDialog(false);
                  setSelectedOptionTitle(null);
                }}
              >
                Sign In
              </Button>
              <Separator
                orientation="vertical"
                className="data-[orientation=vertical]:h-auto self-stretch bg-transparent border-l border-[#FE4D0180]"
              />
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto font-bold text-[#FE4D01] hover:text-[#E44501] text-[0.71875rem] sm:text-xs leading-4 uppercase underline underline-offset-2 hover:decoration-[#E44501]"
                onClick={() => {
                  navigate("/network-plan");
                  setShowDialog(false);
                  setSelectedOptionTitle(null);
                }}
              >
                Plan Builder
              </Button>
            </div>
          </DialogHeader>
          <ul className="space-y-3.5 sm:space-y-4 lg:space-y-5 mb-3.5 sm:mb-4 lg:mb-5">
            {activateOptions.map((option, index) => (
              <li
                key={index}
                className={`relative flex flex-col sm:flex-row sm:items-center gap-2.5 p-3 sm:p-2.5 border rounded-2xl md:rounded-[1.125rem] ${
                  option.isComingSoon
                    ? "bg-[#F2F2F280] cursor-auto"
                    : selectedOptionTitle === option.title
                    ? "bg-[#FE4D0114] border-[#FE4D0166] transition-all duration-300 cursor-pointer"
                    : "bg-[#F9F9F9] border-transparent hover:bg-[#FE4D010D] cursor-pointer"
                }`}
                onClick={() => {
                  if (option.isComingSoon) return;
                  setSelectedOptionTitle(option.title);
                }}
              >
                {option.isComingSoon && (
                  <Badge
                    variant="default"
                    className="absolute -top-2.5 sm:-top-[0.6875rem] lg:-top-3 right-3.5 sm:right-4 bg-[#FEEDE6] hover:bg-[#FEEDE6] h-5 sm:h-[1.375rem] lg:h-6 px-2 py-0.5 border-none rounded-full shadow-none z-10"
                  >
                    <span className="inline-block uppercase font-dmsans text-[0.5625rem] sm:text-[0.625rem] font-semibold leading-[1.125rem] sm:leading-5 text-[#FE4D01] whitespace-nowrap">
                      Coming Soon
                    </span>
                  </Badge>
                )}
                <div
                  className={`shrink-0 flex-grow-0 flex justify-center items-center ${
                    selectedOptionTitle === option.title
                      ? "bg-white transition-all duration-300 "
                      : "bg-[#EAEAEA]"
                  } size-[3.75rem] sm:size-16 lg:size-[4.25rem] rounded-2xl md:rounded-[1.125rem]`}
                >
                  <img
                    src={option.icon}
                    alt={option.altText}
                    className="size-[2.125rem] sm:size-8 lg:size-[1.875rem]"
                  />
                </div>
                <div className="text-left">
                  <h4
                    className={`font-mobile text-black text-[1.0625rem] sm:text-[1.03125rem] lg:text-base font-bold leading-[1.125rem] sm:leading-[1.1875rem] lg:leading-5 my-1 ${
                      selectedOptionTitle === option.title
                        ? "transition-all duration-300"
                        : ""
                    }`}
                  >
                    {option.title.split(/(\(.*?\))|(\*)/).map((part, i) => {
                      if (!part) return null;
                      if (part.startsWith("(") && part.endsWith(")")) {
                        return (
                          <span
                            key={i}
                            className="inline-block text-[#333333] text-[0.9375rem] sm:text-[0.90625rem] lg:text-sm font-normal leading-[1.125rem] sm:leading-[1.0625rem] lg:leading-4"
                          >
                            {part}
                          </span>
                        );
                      }
                      if (part === "*") {
                        return (
                          <span key={i} className="inline-block font-black">
                            {part}
                          </span>
                        );
                      }
                      return part;
                    })}
                  </h4>
                  <p className="text-black/50 text-[0.84375rem] sm:text-[0.78125rem] lg:text-xs font-medium leading-[1.0625rem] sm:leading-[1.125rem]">
                    {option.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-11"
            onClick={handleContinue}
            disabled={!selectedOptionTitle}
          >
            Continue
          </Button>
          <p className="text-left text-xs sm:text-[0.78125rem] lg:text-[0.8125rem] text-[#7C7C7C] font-normal leading-4 sm:leading-[1.125rem] lg:leading-5 mt-3 sm:mt-3.5 lg:mt-4">
            {/* <span className="text-black/60 font-bold">Existing Customers:</span>{" "} */}
            <span className="inline-block font-black">*</span>To activate a
            device on an existing account, please contact{" "}
            <a
              href={createAbsoluteUrl("/support-center")}
              className="font-extrabold underline text-black hover:text-[#FE4D01] transition-colors cursor-pointer"
            >
              customer support.
            </a>
          </p>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};
